import React, {useEffect, useState} from "react";
import {DataTable} from "primereact/datatable";
import {Column} from "primereact/column";
import {Tag} from "primereact/tag";
import {Button} from "primereact/button";
import {Dropdown} from "primereact/dropdown";
import {InputNumber} from "primereact/inputnumber";
import {MultiSelect} from "primereact/multiselect";
import {InputText} from "primereact/inputtext";
import {Dialog} from "primereact/dialog";
import {TabView, TabPanel} from "primereact/tabview";
import {Loading, IndexTemplate} from "../../utils/InlineComponents";
import {AppContext} from "../../HomePage";
import {ProxyMonitorService} from "../../service/ProxyMonitorService";
import {CanaryService} from "../../service/CanaryService";
import AdminPageHeader from "./AdminPageHeader";
import {derivedCaps, proxyServes, asGroups, flatCaps} from "../../utils/ProxyServing";
import {SiteRuleDialog} from "./SiteRuleDialog";

// Read-only view of the in-house unblocker. Two tabs: "Pool" (pool health, per source×type
// counts, a proxy-list search form, and per-IP real-traffic columns joined from stats) and
// "Traffic" (infra-side request metrics from /v1/stats, pivoted client-side by IP / host /
// type / source / country, with an IP→host drill-down = the site-scoping signal).
// Credentials are never exposed — only the IP.
const ProxyMonitor = () => {

    const {t, setError} = React.useContext(AppContext);
    const service = new ProxyMonitorService();
    const canaryService = new CanaryService();

    const [summary, setSummary] = useState(null);
    const [siteList, setSiteList] = useState([]);  // registered tracked hosts, for the scope picker
    const [proxies, setProxies] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filters, setFilters] = useState({source: null, enabled: null, cap: null, serves: null, attention: false});
    const [selectedProxies, setSelectedProxies] = useState([]);
    const [scopeTarget, setScopeTarget] = useState(null);  // {ips:[...]} for the scope-edit dialog
    const [scopeCard, setScopeCard] = useState('capabilities');  // aspect: status|capabilities|only_sites|deny_sites
    const [scopeAdd, setScopeAdd] = useState({capabilities: [], only_sites: [], deny_sites: []});
    const [siteRules, setSiteRules] = useState([]);        // [{host, requires:[...]}]
    const [siteRuleTarget, setSiteRuleTarget] = useState(null);  // rule being edited in the dialog
    // Adaptive selection (success-weighting): which sites it is on for + the per-host weight table.
    const [selectionEnabled, setSelectionEnabled] = useState(true);  // master on/off
    const [selectionHosts, setSelectionHosts] = useState([]);        // enabled_hosts
    const [selectionWeights, setSelectionWeights] = useState({});    // {host: [{ip, weight, ...}]}
    const [selectionHostPick, setSelectionHostPick] = useState(null);// staged host in the add picker
    const [selectionCfg, setSelectionCfg] = useState({});            // key -> value (all config keys)
    const [selectionDesc, setSelectionDesc] = useState({});          // key -> description (from unblocker)
    const [paramEdits, setParamEdits] = useState({});                // local edits for the tuning params
    const [showParams, setShowParams] = useState(false);             // advanced params, collapsed by default
    const [probeUrl, setProbeUrl] = useState('');  // Test & Tag card (inside the edit dialog)
    const [probeCapability, setProbeCapability] = useState('');
    const [probeExpect, setProbeExpect] = useState('');
    const [probeRunning, setProbeRunning] = useState(false);
    const [probeResults, setProbeResults] = useState(null);
    const [logs, setLogs] = useState([]);
    const [showLogs, setShowLogs] = useState(false);
    const [syncing, setSyncing] = useState(false);
    const [syncMsg, setSyncMsg] = useState(null);

    // Traffic tab
    const [statsRows, setStatsRows] = useState([]);
    const [statsSummary, setStatsSummary] = useState(null);
    const [statsDays, setStatsDays] = useState(7);
    const [statsLoading, setStatsLoading] = useState(false);
    const [groupBy, setGroupBy] = useState('ip');
    const [drillIp, setDrillIp] = useState(null);

    // Catalog tab (read-only provider products / prices / balance) — loaded lazily on open.
    const [activeTab, setActiveTab] = useState(0);
    const [store, setStore] = useState(null);
    const [storeLoading, setStoreLoading] = useState(false);
    const [inventoryProvider, setInventoryProvider] = useState(null);  // orders dialog
    const [catalogProduct, setCatalogProduct] = useState(null);        // product plans dialog
    const [quotePeriod, setQuotePeriod] = useState(null);
    const [quoteCountry, setQuoteCountry] = useState(null);
    const [quoteQty, setQuoteQty] = useState(1);
    const [quote, setQuote] = useState(null);
    const [quoteLoading, setQuoteLoading] = useState(false);
    const [iproyalPlan, setIproyalPlan] = useState(null);
    const [iproyalQty, setIproyalQty] = useState(1);
    const [comparison, setComparison] = useState([]);
    const [comparisonLoading, setComparisonLoading] = useState(false);

    useEffect(() => {
        load();
        loadSiteRules();
        loadSelection();
        // getHosts returns [{host, proxy_type, ...}] objects — reduce to bare host strings.
        canaryService.getHosts()
            .then(hosts => setSiteList((hosts || [])
                .map(h => (typeof h === 'string' ? h : h && h.host)).filter(Boolean)))
            .catch(() => {});
    }, []);

    const loadSiteRules = () => service.getSiteRules()
        .then(d => setSiteRules((d && d.rules) || [])).catch(() => {});

    // ---- adaptive selection config (enabled_hosts + master toggle + tuning params) ----
    const SELECTION_PARAMS = ['window_days', 'decay', 'prior_strength', 'temperature',
        'min_weight', 'prior_alpha', 'prior_beta'];
    const loadSelection = () => service.getSelection().then(d => {
        const cfg = {}, desc = {};
        ((d && d.config) || []).forEach(c => { cfg[c.key] = c.value; desc[c.key] = c.description; });
        setSelectionEnabled(cfg.enabled !== false);
        setSelectionHosts(cfg.enabled_hosts || []);
        setSelectionCfg(cfg);
        setSelectionDesc(desc);
        setParamEdits({});
        setSelectionWeights((d && d.hosts) || {});
    }).catch(() => {});
    // Save only the tuning params the admin actually changed.
    const paramChanged = (k) => k in paramEdits && paramEdits[k] !== selectionCfg[k];
    const saveSelectionParams = () => {
        const changed = SELECTION_PARAMS.filter(paramChanged);
        if (!changed.length) { setShowParams(false); return; }
        Promise.all(changed.map(k => service.setSelectionConfig(k, paramEdits[k])))
            .then(() => loadSelection()).then(() => setShowParams(false))
            .catch(error => { setError(error); setError(null); });
    };
    const closeParams = () => { setParamEdits({}); setShowParams(false); };

    const setSelectionHostsRemote = (hosts) => service.setSelectionConfig('enabled_hosts', hosts)
        .then(() => loadSelection()).catch(error => { setError(error); setError(null); });
    const addSelectionHost = (host) => {
        const h = (host || '').trim().toLowerCase();
        if (!h || selectionHosts.includes(h)) return;
        setSelectionHostsRemote([...selectionHosts, h]);
        setSelectionHostPick(null);
    };
    const removeSelectionHost = (host) => setSelectionHostsRemote(selectionHosts.filter(h => h !== host));
    const toggleSelectionEnabled = () => service.setSelectionConfig('enabled', !selectionEnabled)
        .then(() => loadSelection()).catch(error => { setError(error); setError(null); });

    const syncNow = () => {
        setSyncing(true);
        setSyncMsg(null);
        service.syncNow().then(res => {
            setSyncing(false);
            setSyncMsg(res && res.error
                ? res.error
                : (t('proxy_monitor.sync_now') + ': +' + ((res && res.added) || 0)
                    + ' / -' + ((res && res.disabled) || 0)));
            load();
        }).catch(error => { setError(error); setError(null); setSyncing(false); });
    };

    const loadStore = () => {
        setStoreLoading(true);
        service.getStore().then(data => {
            setStore(data);
            setStoreLoading(false);
            buildComparison(data);
        }).catch(error => { setError(error); setError(null); setStoreLoading(false); });
    };

    const onTabChange = (index) => {
        setActiveTab(index);
        if (index === 3 && store === null) loadStore();  // Catalog tab (now 4th): load once
    };

    const openProduct = (p) => {
        setCatalogProduct(p); setQuote(null);
        setQuotePeriod(null); setQuoteCountry(null); setQuoteQty(1);
        setIproyalPlan(null); setIproyalQty(1);
    };

    // IPRoyal price is static (plan unit price + volume discount tiers) -> compute client-side.
    const bestDiscount = (tiers, qty) => (tiers || []).reduce((best, tier) =>
        (tier.quantity_from <= qty && (tier.discount_percent || 0) > best) ? tier.discount_percent : best, 0);
    const iproyalCalc = () => {
        if (!catalogProduct || iproyalPlan == null) return null;
        const plan = (catalogProduct.plans || []).find(pl => pl.id === iproyalPlan);
        if (!plan || plan.price == null) return null;
        const qty = Math.max(1, iproyalQty || 1);
        const disc = bestDiscount(catalogProduct.discountTiers, qty);
        const per = plan.price * (1 - disc / 100);
        return {total: per * qty, per, disc, currency: catalogProduct.currency || 'USD'};
    };

    // Fixed side-by-side comparison (100 IP, Turkey) for datacenter + ISP: IPRoyal computed
    // client-side, proxy-seller via order/calc. Runs when the catalog loads.
    const buildComparison = (data) => {
        const provs = (data && data.providers) || [];
        const ipList = ((provs.find(p => p.provider === 'iproyal') || {}).products) || [];
        const psList = ((provs.find(p => p.provider === 'proxyseller') || {}).products) || [];
        const QTY = 100;
        const scenarios = [
            {key: 'dc', label: 'Datacenter', ipMatch: 'datacenter', psType: 'ipv4'},
            {key: 'isp', label: 'ISP', ipMatch: 'isp', psType: 'isp'},
        ];
        const rows = scenarios.map(s => {
            let iproyal = null;
            const ip = ipList.find(p => (p.name || '').toLowerCase().includes(s.ipMatch));
            if (ip) {
                const plan = (ip.plans || []).find(pl => /30/.test(pl.name || '') && pl.price != null)
                    || (ip.plans || []).find(pl => pl.price != null);
                if (plan) {
                    const disc = bestDiscount(ip.quantity_discounts, QTY);
                    const trLoc = (ip.locations || []).find(l => /turk|türk/i.test(l.name || '') && !l.out_of_stock);
                    iproyal = {total: plan.price * QTY * (1 - disc / 100), currency: 'USD', tr: !!trLoc};
                }
            }
            let psReq = null, psWhy = null;
            const ps = psList.find(p => (p.name || '').toLowerCase() === s.psType);
            if (!ps) {
                psWhy = 'no "' + s.psType + '" product';
            } else {
                const trC = (ps.locations || []).find(l => /turk|türk/i.test(l.name || ''));
                const per = (ps.plans || []).find(pl => pl.id === '1m' || /1\s*month|30/i.test(pl.name || ''))
                    || (ps.plans || [])[0];
                if (!trC) psWhy = 'TR not offered (' + (ps.locations || []).length + ' countries)';
                else if (!per) psWhy = 'no period (' + (ps.plans || []).length + ' plans)';
                else psReq = {type: s.psType, country: trC.id, period: per.id, quantity: QTY};
            }
            return {key: s.key, label: s.label, iproyal, psReq, psWhy, proxyseller: null};
        });
        setComparison(rows);
        setComparisonLoading(true);
        Promise.all(rows.map(r => r.psReq
            ? service.getQuote(r.psReq).then(q => ({key: r.key, q})).catch(() => ({key: r.key, q: null}))
            : Promise.resolve({key: r.key, q: null})))
            .then(results => {
                setComparison(prev => prev.map(r => {
                    const res = results.find(x => x.key === r.key) || {};
                    const ok = res.q && !res.q.error;
                    return {...r, proxyseller: ok ? res.q : null,
                            psError: ok ? null : ((res.q && res.q.error) || r.psWhy || 'no result')};
                }));
                setComparisonLoading(false);
            });
    };
    const calcQuote = () => {
        if (!catalogProduct || quotePeriod == null || quoteCountry == null) return;
        setQuoteLoading(true);
        service.getQuote({type: catalogProduct.product, country: quoteCountry,
                          period: quotePeriod, quantity: quoteQty || 1})
            .then(data => { setQuote(data); setQuoteLoading(false); })
            .catch(error => { setError(error); setError(null); setQuoteLoading(false); });
    };

    const catalogProducts = () => {
        const list = [];
        ((store && store.providers) || []).forEach(pr => {
            (pr.products || []).forEach(prod => {
                const tr = (prod.locations || []).find(l => /turk|türk/i.test(l.name || ''));
                const disc = (prod.quantity_discounts || [])
                    .map(q => `${q.quantity_from}+ → ${q.discount_percent}%`).join(', ');
                const prices = (prod.plans || []).map(pl => pl.price).filter(p => p != null);
                list.push({
                    key: pr.provider + '|' + prod.name, provider: pr.provider,
                    currency: pr.currency, product: prod.name, plans: prod.plans || [],
                    planCount: (prod.plans || []).length, locations: prod.locations || [],
                    minPrice: prices.length ? Math.min(...prices) : null,
                    maxPrice: prices.length ? Math.max(...prices) : null,
                    discounts: disc, discountTiers: prod.quantity_discounts || [],
                    tr: tr ? (tr.out_of_stock ? 'out' : 'in') : 'unknown',
                });
            });
        });
        return list;
    };

    const trTag = (tr) => tr === 'in'
        ? <Tag value={t('proxy_monitor.in_stock')} severity="success"/>
        : (tr === 'out' ? <Tag value={t('proxy_monitor.out_of_stock')} severity="danger"/> : <span>-</span>);

    // Provider colour + product icon for the catalog cards.
    const providerStyle = (provider) => provider === 'iproyal'
        ? {bg: '#E6F1FB', fg: '#185FA5'} : {bg: '#EEEDFE', fg: '#534AB7'};
    const productIcon = (name) => {
        const n = (name || '').toLowerCase();
        if (n.includes('datacenter')) return 'pi-server';
        if (n.includes('residential')) return 'pi-home';
        if (n.includes('isp')) return 'pi-sitemap';
        if (n.includes('unblocker') || n.includes('web')) return 'pi-shield';
        if (n.includes('ipv')) return 'pi-globe';
        return 'pi-box';
    };

    // Region availability from a product's countries: green if at least one in-stock country
    // in that region is offered, else red.
    const EU_SET = new Set(['austria', 'belgium', 'bulgaria', 'croatia', 'cyprus', 'czechia',
        'czech republic', 'denmark', 'estonia', 'finland', 'france', 'germany', 'greece', 'hungary',
        'ireland', 'italy', 'latvia', 'lithuania', 'luxembourg', 'malta', 'netherlands', 'poland',
        'portugal', 'romania', 'slovakia', 'slovenia', 'spain', 'sweden']);
    const SA_SET = new Set(['argentina', 'bolivia', 'brazil', 'chile', 'colombia', 'ecuador',
        'guyana', 'paraguay', 'peru', 'suriname', 'uruguay', 'venezuela']);
    const regionMatch = {
        TR: (n) => /turk|türk/i.test(n),
        US: (n) => /united states/i.test(n) || ['usa', 'us'].includes(n.trim().toLowerCase()),
        EU: (n) => EU_SET.has(n.trim().toLowerCase()),
        SA: (n) => SA_SET.has(n.trim().toLowerCase()),
    };
    const regionAvailable = (p, r) => (p.locations || []).some(
        loc => !loc.out_of_stock && regionMatch[r](loc.name || ''));
    const regionTags = (p) => [['TR', 'TR'], ['US', 'US'], ['EU', 'EU'], ['SA', t('proxy_monitor.region_sa')]]
        .map(([r, label]) => <Tag key={r} value={label}
                                  severity={regionAvailable(p, r) ? 'success' : 'danger'}/>);

    const ownedRows = () => {
        const rows = [];
        ((store && store.providers) || []).forEach(pr => {
            (((pr.owned || {}).orders) || []).forEach(o => rows.push({
                provider: pr.provider, currency: pr.currency, product: o.product_name,
                plan: o.plan_name, quantity: o.quantity, est_cost: o.est_cost,
                expire_date: o.expire_date, auto_renew: o.auto_renew, status: o.status,
            }));
        });
        return rows;
    };

    const load = () => {
        setLoading(true);
        service.getPool().then(data => {
            setSummary(data && data.summary);
            setProxies((data && data.proxies) || []);
            setLogs((data && data.logs) || []);
            setLoading(false);
        }).catch(error => { setError(error); setError(null); setLoading(false); });
        loadStats(statsDays);
    };

    const loadStats = (days) => {
        setStatsLoading(true);
        service.getStats(days).then(data => {
            setStatsRows((data && data.rows) || []);
            setStatsSummary((data && data.summary) || null);
            setStatsLoading(false);
        }).catch(error => { setError(error); setError(null); setStatsLoading(false); });
    };

    const onDaysChange = (v) => { setStatsDays(v); loadStats(v); };

    const distinct = (key) => [...new Set(proxies.map(p => p[key]).filter(v => v != null))]
        .map(v => ({label: v, value: v}));
    const flatDistinct = (key) => [...new Set(proxies.flatMap(p => p[key] || []))]
        .map(v => ({label: v, value: v}));
    // Host picker (only_sites / deny_sites): registered tracked hosts ∪ hosts already scoped.
    const siteOptions = [...new Set([...siteList,
        ...proxies.flatMap(p => [...(p.deny_sites || []), ...(p.only_sites || [])])])]
        .sort().map(v => ({label: v, value: v}));
    // Capability suggestions: stored tags ∪ site_rules ∪ attribute-derived tokens
    // (type:/source:/geo:, auto-available on every proxy) ∪ a few seeds.
    const capabilityOptions = [...new Set([
        ...proxies.flatMap(p => p.capabilities || []),
        ...siteRules.flatMap(r => flatCaps(r.requires)),
        ...proxies.flatMap(p => [p.type && ('type:' + p.type), p.source && ('source:' + p.source),
            p.country && ('geo:' + String(p.country).toLowerCase())].filter(Boolean)),
        'hb-ok', 'pazarama-ok'])].sort();

    const applyFilters = (list, f) => (list || []).filter(p => {
        if (f.source && p.source !== f.source) return false;
        if (f.enabled != null && p.enabled !== f.enabled) return false;
        if (f.cap && !((p.capabilities || []).includes(f.cap))) return false;
        if (f.serves) {
            const r = siteRules.find(x => x.host === f.serves);
            if (!proxyServes(p, f.serves, (r && r.requires) || [])) return false;
        }
        if (f.attention && !needsAttention(p)) return false;
        return true;
    });

    // Reload the pool after a mutation (the displayed list re-derives from the new proxies).
    const reloadPool = () => service.getPool().then(data => {
        setProxies((data && data.proxies) || []);
        setSummary(data && data.summary);
        setLogs((data && data.logs) || []);
        setSelectedProxies([]);
    });

    // Admin manual enable/disable (single or bulk) by egress IP. Enable is a fresh start
    // (resets the fail streak) but stays subject to the health-check. Reloads + re-filters.
    const setEnabled = (ips, enabled) => {
        if (!ips || !ips.length) return;
        service.setProxies(ips, enabled).then(reloadPool)
            .catch(error => { setError(error); setError(null); });
    };

    // Open the scope-edit dialog for one or more proxies (by egress IP).
    const openScope = (ips) => {
        if (!ips || !ips.length) return;
        setScopeCard('capabilities');
        setScopeAdd({capabilities: [], only_sites: [], deny_sites: []});
        setProbeUrl(''); setProbeCapability(''); setProbeExpect(''); setProbeResults(null); setProbeRunning(false);
        setScopeTarget({ips});
    };

    // Live add: append the staged values to `listName` on every selected proxy, then clear.
    const addScope = (listName) => {
        const ips = (scopeTarget && scopeTarget.ips) || [];
        const vals = (scopeAdd[listName] || []).filter(Boolean);
        if (!ips.length || !vals.length) return;
        service.setScope(ips, listName, 'add', vals)
            .then(() => reloadPool()).then(() => setScopeAdd(s => ({...s, [listName]: []})))
            .catch(error => { setError(error); setError(null); });
    };
    // Live remove (batch): drop the staged values from `listName` on every selected proxy that
    // carries them ($pull is a no-op on proxies that don't). Mirror of addScope for the Remove button.
    const removeScope = (listName) => {
        const ips = (scopeTarget && scopeTarget.ips) || [];
        const vals = (scopeAdd[listName] || []).filter(Boolean);
        if (!ips.length || !vals.length) return;
        service.setScope(ips, listName, 'remove', vals)
            .then(() => reloadPool()).then(() => setScopeAdd(s => ({...s, [listName]: []})))
            .catch(error => { setError(error); setError(null); });
    };
    // Live remove: drop one value from `listName` on every selected proxy that carries it (chip ✕).
    const removeScopeVal = (listName, value) => {
        const ips = (scopeTarget && scopeTarget.ips) || [];
        if (!ips.length) return;
        service.setScope(ips, listName, 'remove', [value]).then(reloadPool)
            .catch(error => { setError(error); setError(null); });
    };

    // ---- site_rules (site -> required capabilities) ----
    const saveSiteRule = () => {
        const host = ((siteRuleTarget && siteRuleTarget.host) || '').trim();
        if (!host) return;
        // Denormalize the editor's groups: trim caps, drop empty groups; 1 group -> flat list
        // (backward-compatible, tidy), 2+ groups -> OR'd list-of-lists, 0 -> [] (deletes the rule).
        const groups = (siteRuleTarget.groups || [])
            .map(g => (g || []).map(c => (c || '').trim()).filter(Boolean))
            .filter(g => g.length);
        const requires = groups.length === 0 ? [] : (groups.length === 1 ? groups[0] : groups);
        // Per-host concurrency cap: blank/0 = uncapped (the panel is authoritative, so send 0 to clear).
        const maxConcurrency = Math.max(0, parseInt(siteRuleTarget.max_concurrency, 10) || 0);
        service.setSiteRule(host, requires, !!siteRuleTarget.requires_render, maxConcurrency)
            .then(() => loadSiteRules()).then(() => setSiteRuleTarget(null))
            .catch(error => { setError(error); setError(null); });
    };
    // Empty requires deletes the rule (the site reopens to every eligible proxy).
    const removeSiteRule = (host) => {
        service.setSiteRule(host, []).then(loadSiteRules)
            .catch(error => { setError(error); setError(null); });
    };

    // ---- probe-and-tag: test selected proxies against a URL, tag the passers ----
    const runProbe = () => {
        const ips = (scopeTarget && scopeTarget.ips) || [];
        if (!ips.length || !probeUrl.trim() || !probeCapability.trim()) return;
        setProbeRunning(true); setProbeResults(null);
        service.probe({ips, url: probeUrl.trim(), capability: probeCapability.trim(), expect: probeExpect.trim()})
            .then(r => { setProbeResults(r); setProbeRunning(false); return reloadPool(); })
            .catch(error => { setProbeRunning(false); setError(error); setError(null); });
    };

    // ---- metric helpers ----
    const pct = (num, den) => den ? (100 * num / den) : 0;
    const rateTag = (p) => {
        const sev = p >= 98 ? 'success' : (p >= 95 ? 'warning' : 'danger');
        return <Tag value={p.toFixed(1) + '%'} severity={sev}/>;
    };
    const avgLatency = (r) => r.attempts ? Math.round(r.latency_ms_sum / r.attempts) + ' ms' : '-';

    // Aggregate raw (user,ip,host) rows into one row per value of `dim`.
    const pivot = (rows, dim) => {
        const map = {};
        (rows || []).forEach(r => {
            const key = r[dim] == null ? '' : r[dim];
            let e = map[key];
            if (!e) e = map[key] = {key, attempts: 0, success: 0, ban: 0, error: 0, timeout: 0, latency_ms_sum: 0};
            e.attempts += r.attempts || 0; e.success += r.success || 0; e.ban += r.ban || 0;
            e.error += r.error || 0; e.timeout += r.timeout || 0; e.latency_ms_sum += r.latency_ms_sum || 0;
        });
        return Object.values(map).sort((a, b) => b.attempts - a.attempts);
    };

    // ip -> aggregated stats, to join real traffic onto the pool list.
    const ipStats = {};
    pivot(statsRows, 'ip').forEach(e => { ipStats[e.key] = e; });

    // ---- Pool tab bodies ----
    const statusBody = (p) => p.enabled
        ? <Tag value={t('proxy_monitor.enabled')} severity="success"/>
        : <Tag severity={p.removed ? 'warning' : 'danger'}
               value={p.removed ? t('proxy_monitor.removed')
                   : (p.health_disabled ? t('proxy_monitor.health_disabled') : t('proxy_monitor.disabled'))}/>;
    // ---- Serves: which sites this proxy is routed to (capabilities + derived + site_rules) ----
    // derivedCaps / proxyServes are shared with the Canary screen — see utils/ProxyServing.
    const shortHost = (h) => (h || '').replace(/^www\./, '').replace(/\.(com|net|org|co)(\.tr)?$/i, '');
    const proxyBody = (p) => (
        <div>
            <div style={{fontWeight: 500}}>{p.ip}</div>
            <div style={{fontSize: '0.8em', color: 'var(--text-color-secondary)'}}>
                {[p.source, p.type, p.country].filter(Boolean).join(' · ')}</div>
        </div>
    );
    // Read-only routing: protected sites (site_rules) as served (green) / not-served (struck),
    // plus a note for the open sites everyone serves.
    const servesBody = (p) => {
        if (!p.enabled) return <span style={{color: 'var(--text-color-secondary)'}}>-</span>;
        const chips = siteRules.map(r => {
            const ok = proxyServes(p, r.host, r.requires || []);
            return <span key={r.host} title={r.host}
                         style={{fontSize: '0.78em', padding: '1px 8px', borderRadius: '12px', whiteSpace: 'nowrap',
                                 background: ok ? 'rgba(34,197,94,0.14)' : 'transparent',
                                 color: ok ? '#16a34a' : 'var(--text-color-secondary)',
                                 textDecoration: ok ? 'none' : 'line-through'}}>{shortHost(r.host)}</span>;
        });
        const note = (p.only_sites || []).length
            ? t('proxy_monitor.reserved')
            : ('+ ' + t('proxy_monitor.open_sites'));
        return <span style={{display: 'flex', flexWrap: 'wrap', gap: '4px', alignItems: 'center'}}>
            {chips}<span style={{fontSize: '0.78em', color: 'var(--text-color-secondary)'}}>{note}</span></span>;
    };
    // capability = green, only_sites = purple, deny = red. Each is a removable chip (✕).
    const _CHIP_COLORS = {
        capabilities: ['rgba(34,197,94,0.14)', '#16a34a'],
        only_sites: ['rgba(168,85,247,0.14)', '#a855f7'],
        deny_sites: ['rgba(239,68,68,0.12)', '#ef4444'],
    };
    const reqBody = (p) => { const e = ipStats[p.ip]; return e ? e.attempts.toLocaleString() : '-'; };
    const poolSuccessBody = (p) => { const e = ipStats[p.ip]; return e ? rateTag(pct(e.success, e.attempts)) : <span>-</span>; };

    // ---- Traffic tab bodies ----
    const groupBody = (row) => (groupBy === 'ip')
        ? <span style={{cursor: 'pointer', textDecoration: 'underline'}}
                onClick={() => setDrillIp(row.key)}>{row.key || '-'}</span>
        : <span>{row.key || '-'}</span>;
    const reqCol = (row) => (row.attempts || 0).toLocaleString();
    const successCol = (row) => rateTag(pct(row.success, row.attempts));
    const banCol = (row) => <span>{pct(row.ban, row.attempts).toFixed(1)}%</span>;
    const latencyCol = (row) => avgLatency(row);

    const statusOptions = [{label: t('proxy_monitor.enabled'), value: true},
                           {label: t('proxy_monitor.disabled'), value: false}];
    const daysOptions = [{label: t('proxy_monitor.last_1'), value: 1},
                         {label: t('proxy_monitor.last_7'), value: 7},
                         {label: t('proxy_monitor.last_30'), value: 30}];
    const groupOptions = ['ip', 'host', 'type', 'source', 'country']
        .map(k => ({label: t('proxy_monitor.' + k), value: k}));

    const dd = (key, options) => (
        <Dropdown value={filters[key]} options={options} showClear={true} filter={true}
                  placeholder={t('proxy_monitor.all')} style={{width: '100%'}}
                  onChange={(e) => setFilters({...filters, [key]: e.value})}/>
    );

    const trafficColumns = (dt) => [
        <Column key="req" header={t('proxy_monitor.requests')} body={reqCol} sortable={true} sortField="attempts"/>,
        <Column key="suc" header={t('proxy_monitor.success_rate')} body={successCol}/>,
        <Column key="ban" header={t('proxy_monitor.ban_rate')} body={banCol}/>,
        <Column key="err" field="error" header={t('proxy_monitor.errors')} sortable={true}/>,
        <Column key="to" field="timeout" header={t('proxy_monitor.timeouts')} sortable={true}/>,
        <Column key="lat" header={t('proxy_monitor.avg_latency')} body={latencyCol}/>,
    ];


    const summaryTags = statsSummary && (() => {
        const s = statsSummary;
        return <div style={{display: 'flex', gap: '0.6em', alignItems: 'center', margin: '0.5em 0 1.5em', flexWrap: 'wrap'}}>
            <Tag value={t('proxy_monitor.total_requests') + ': ' + (s.attempts || 0).toLocaleString()}/>
            <Tag value={t('proxy_monitor.success_rate') + ': ' + pct(s.success, s.attempts).toFixed(1) + '%'}
                 severity={pct(s.success, s.attempts) >= 98 ? 'success' : (pct(s.success, s.attempts) >= 95 ? 'warning' : 'danger')}/>
            <Tag value={t('proxy_monitor.avg_latency') + ': ' + (s.attempts ? Math.round(s.latency_ms_sum / s.attempts) : 0) + ' ms'}/>
        </div>;
    })();

    // A proxy "needs attention" when it's off (auto-disabled/removed/manual) or failing checks.
    const needsAttention = (p) => !p.enabled || (p.consecutive_fails || 0) > 0;
    const displayed = applyFilters(proxies, filters);
    const serveOptions = siteRules.map(r => ({label: shortHost(r.host), value: r.host}));
    const attentionCount = proxies.filter(needsAttention).length;
    // Pool composition per provider (sum of enabled+disabled from the summary groups).
    const providerCounts = Object.values((((summary && summary.groups) || [])).reduce((m, g) => {
        (m[g.source] = m[g.source] || {source: g.source, count: 0}).count += (g.enabled || 0) + (g.disabled || 0);
        return m;
    }, {}));

    return <React.Fragment>
        <div className="card management-page">
            <AdminPageHeader
                title={t('menu_item.proxy_monitor')}
                subtitle={t('menu.management_hub.page.proxy_monitor')}
            />
            {syncMsg &&
                <div style={{margin: '0 0 1em', color: 'var(--text-color-secondary)', fontSize: '0.9em'}}>{syncMsg}</div>}

            <Dialog header={t('proxy_monitor.activity')} visible={showLogs}
                    style={{maxWidth: '55vw', minWidth: '25vw'}} breakpoints={{'960px': '85vw', '640px': '95vw'}}
                    modal={true} onHide={() => setShowLogs(false)}>
                <DataTable value={logs} emptyMessage={t('proxy_monitor.no_logs')}
                           paginator={logs.length > 10} rows={10}>
                    <Column field="at" header={t('proxy_monitor.when')} sortable={true}
                            body={(l) => l.at ? new Date(l.at).toLocaleString() : '-'}/>
                    <Column field="job" header={t('proxy_monitor.job')} sortable={true}/>
                    <Column field="summary" header={t('proxy_monitor.summary')}/>
                </DataTable>
            </Dialog>

            <Dialog header={t('proxy_monitor.host_breakdown') + (drillIp ? ' — ' + drillIp : '')} visible={drillIp != null}
                    style={{maxWidth: '55vw', minWidth: '25vw'}} breakpoints={{'960px': '85vw', '640px': '95vw'}}
                    modal={true} onHide={() => setDrillIp(null)}>
                <DataTable value={drillIp ? pivot(statsRows.filter(r => r.ip === drillIp), 'host') : []}
                           emptyMessage={t('proxy_monitor.no_traffic')} dataKey="key"
                           paginator={true} rows={10}>
                    <Column field="key" header={t('proxy_monitor.host')} sortable={true}/>
                    {trafficColumns()}
                </DataTable>
            </Dialog>

            <SiteRuleDialog target={siteRuleTarget} setTarget={setSiteRuleTarget} onSave={saveSiteRule}
                            capabilityOptions={capabilityOptions} siteOptions={siteOptions}/>

            <Dialog header={t('proxy_monitor.adaptive_params')} visible={showParams}
                    style={{width: '44vw', maxWidth: '560px', minWidth: '30vw'}} breakpoints={{'960px': '85vw', '640px': '95vw'}}
                    modal={true} onHide={closeParams}
                    footer={<span>
                        <Button label={t('actions.cancel')} icon="pi pi-times" className="p-button-text" onClick={closeParams}/>
                        <Button label={t('proxy_monitor.adaptive_params_save')} icon="pi pi-check" className="p-button-success"
                                onClick={saveSelectionParams} disabled={!SELECTION_PARAMS.some(paramChanged)}/>
                    </span>}>
                <div style={{display: 'flex', flexDirection: 'column'}}>
                    <span style={{color: 'var(--text-color-secondary)', fontSize: '0.85em', marginBottom: '0.5em'}}>
                        {t('proxy_monitor.adaptive_params_hint')}</span>
                    {SELECTION_PARAMS.map(k => (
                        <div key={k} style={{padding: '0.65em 0', borderTop: '1px solid var(--surface-border)'}}>
                            <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1em'}}>
                                <label style={{fontFamily: 'monospace', fontWeight: 600, fontSize: '0.92em'}}>{k}</label>
                                <InputNumber value={k in paramEdits ? paramEdits[k] : selectionCfg[k]}
                                             onValueChange={(e) => setParamEdits(s => ({...s, [k]: e.value}))}
                                             mode="decimal" minFractionDigits={0}
                                             maxFractionDigits={k === 'window_days' ? 0 : 3}
                                             useGrouping={false} inputStyle={{width: '7em', textAlign: 'right'}}
                                             style={{flexShrink: 0}}/>
                            </div>
                            <div style={{fontSize: '0.8em', color: 'var(--text-color-secondary)',
                                         lineHeight: 1.4, marginTop: '4px'}}>{selectionDesc[k]}</div>
                        </div>
                    ))}
                </div>
            </Dialog>

            <Dialog header={t('proxy_monitor.edit_scope')} visible={scopeTarget != null}
                    style={{maxWidth: '46vw', minWidth: '28vw'}} breakpoints={{'960px': '85vw', '640px': '95vw'}}
                    modal={true} onHide={() => setScopeTarget(null)}
                    footer={<Button label={t('proxy_monitor.done')} icon="pi pi-check" className="p-button-success"
                                    onClick={() => setScopeTarget(null)}/>}>
                {scopeTarget && (() => {
                    const tps = proxies.filter(x => scopeTarget.ips.includes(x.ip));
                    const union = (ln) => [...new Set(tps.flatMap(x => x[ln] || []))];
                    const derived = [...new Set(tps.flatMap(derivedCaps))];
                    const chip = (ln, v) => {
                        const c = _CHIP_COLORS[ln];
                        return <span key={ln + ':' + v} style={{display: 'inline-flex', alignItems: 'center', gap: '4px',
                                     fontSize: '0.8em', borderRadius: '12px', padding: '2px 5px 2px 10px', whiteSpace: 'nowrap',
                                     background: c[0], color: c[1]}}>
                            {v}<i className="pi pi-times-circle" title={t('proxy_monitor.op_remove')}
                                  style={{cursor: 'pointer', fontSize: '0.95em', opacity: 0.8}}
                                  onClick={() => removeScopeVal(ln, v)}/></span>;
                    };
                    const section = (ln, options, placeholder) => (
                        <div>
                            <label style={{fontSize: '0.85em', fontWeight: 500}}>{t('proxy_monitor.' + ln)}</label>
                            <div style={{display: 'flex', flexWrap: 'wrap', gap: '4px', margin: '5px 0 7px'}}>
                                {union(ln).map(v => chip(ln, v))}
                                {ln === 'capabilities' && derived.map(v =>
                                    <span key={'d:' + v} title={t('proxy_monitor.derived')}
                                          style={{fontSize: '0.8em', color: 'var(--text-color-secondary)', whiteSpace: 'nowrap',
                                                  border: '0.5px dashed var(--surface-border)', borderRadius: '12px', padding: '1px 9px'}}>{v}</span>)}
                                {!union(ln).length && !(ln === 'capabilities' && derived.length) &&
                                    <span style={{fontSize: '0.8em', color: 'var(--text-color-secondary)'}}>—</span>}
                            </div>
                            <div style={{display: 'flex', gap: '0.5em', alignItems: 'stretch'}}>
                                <MultiSelect value={scopeAdd[ln]} options={options} display="chip" filter={true}
                                             onChange={(e) => setScopeAdd(s => ({...s, [ln]: e.value}))}
                                             placeholder={placeholder} style={{flex: 1, minWidth: 0}}/>
                                <Button label={t('proxy_monitor.op_add')} icon="pi pi-plus" className="p-button-sm"
                                        style={{width: 'auto', flexShrink: 0}}
                                        onClick={() => addScope(ln)} disabled={!scopeAdd[ln].length}/>
                                <Button label={t('proxy_monitor.op_remove')} icon="pi pi-minus"
                                        className="p-button-sm p-button-outlined p-button-danger"
                                        style={{width: 'auto', flexShrink: 0}}
                                        onClick={() => removeScope(ln)} disabled={!scopeAdd[ln].length}/>
                            </div>
                            <small style={{color: 'var(--text-color-secondary)', marginTop: '5px', display: 'block'}}>
                                {t('proxy_monitor.add_remove_hint')}</small>
                        </div>
                    );
                    const enabledN = tps.filter(x => x.enabled).length;
                    const cardCount = (k) => k === 'capabilities' ? union('capabilities').length + derived.length : union(k).length;
                    const cards = [
                        {key: 'status', label: t('proxy_monitor.status'), icon: 'pi pi-power-off', color: '#0d9488',
                         bg: 'rgba(13,148,136,0.12)', desc: t('proxy_monitor.card_desc_status'), badge: enabledN + '/' + tps.length},
                        {key: 'capabilities', label: t('proxy_monitor.capabilities'), icon: 'pi pi-tag', color: '#16a34a',
                         bg: 'rgba(34,197,94,0.14)', desc: t('proxy_monitor.card_desc_capabilities'), badge: cardCount('capabilities')},
                        {key: 'only_sites', label: t('proxy_monitor.only_sites'), icon: 'pi pi-lock', color: '#a855f7',
                         bg: 'rgba(168,85,247,0.14)', desc: t('proxy_monitor.card_desc_only_sites'), badge: cardCount('only_sites')},
                        {key: 'deny_sites', label: t('proxy_monitor.deny_sites'), icon: 'pi pi-ban', color: '#ef4444',
                         bg: 'rgba(239,68,68,0.12)', desc: t('proxy_monitor.card_desc_deny_sites'), badge: cardCount('deny_sites')},
                        {key: 'test_tag', label: t('proxy_monitor.test_tag'), icon: 'pi pi-bolt', color: '#f59e0b',
                         bg: 'rgba(245,158,11,0.14)', desc: t('proxy_monitor.card_desc_test_tag'), badge: null},
                    ];
                    return <div className="p-fluid" style={{display: 'flex', flexDirection: 'column', gap: '1em'}}>
                        <span style={{color: 'var(--text-color-secondary)', fontSize: '0.9em'}}>
                            {scopeTarget.ips.length} {t('proxy_monitor.selected')} — {scopeTarget.ips.slice(0, 6).join(', ')}
                            {scopeTarget.ips.length > 6 ? ' …' : ''}</span>
                        <div style={{display: 'flex', gap: '8px'}}>
                            {cards.map(c => {
                                const active = scopeCard === c.key;
                                return <div key={c.key} onClick={() => setScopeCard(c.key)}
                                            style={{cursor: 'pointer', flex: '1 1 0', minWidth: 0,
                                                    padding: '9px 11px', borderRadius: '10px',
                                                    border: '1px solid ' + (active ? c.color : 'var(--surface-border)'),
                                                    background: active ? c.bg : 'transparent',
                                                    boxShadow: active ? 'inset 0 0 0 1px ' + c.color : 'none',
                                                    transition: 'border-color .12s, background .12s'}}>
                                    <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '6px'}}>
                                        <span style={{display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.85em', fontWeight: 600,
                                                      color: active ? 'var(--text-color)' : 'var(--text-color-secondary)'}}>
                                            <i className={c.icon} style={{fontSize: '0.95em', color: c.color}}/>{c.label}</span>
                                        {c.badge != null &&
                                            <span style={{fontSize: '0.72em', fontWeight: 700, color: c.color, background: c.bg,
                                                          borderRadius: '9px', padding: '0 7px', lineHeight: '1.5', whiteSpace: 'nowrap'}}>{c.badge}</span>}
                                    </div>
                                    <div style={{fontSize: '0.74em', lineHeight: 1.25, marginTop: '5px',
                                                 color: 'var(--text-color-secondary)'}}>{c.desc}</div>
                                </div>;
                            })}
                        </div>
                        <div style={{minHeight: '250px'}}>
                        {scopeCard === 'status'
                            ? <div>
                                <div style={{fontSize: '0.9em', marginBottom: '10px', color: 'var(--text-color-secondary)'}}>
                                    {enabledN} {t('proxy_monitor.enabled')}
                                    {tps.length - enabledN ? ' · ' + (tps.length - enabledN) + ' ' + t('proxy_monitor.disabled') : ''}</div>
                                <div style={{display: 'flex', gap: '0.5em'}}>
                                    <Button label={t('proxy_monitor.enable')} icon="pi pi-check" className="p-button-outlined p-button-sm"
                                            onClick={() => setEnabled(scopeTarget.ips, true)}/>
                                    <Button label={t('proxy_monitor.disable')} icon="pi pi-times" className="p-button-outlined p-button-danger p-button-sm"
                                            onClick={() => setEnabled(scopeTarget.ips, false)}/>
                                </div>
                            </div>
                            : scopeCard === 'test_tag'
                            ? <div className="p-fluid" style={{display: 'flex', flexDirection: 'column', gap: '0.8em'}}>
                                <div>
                                    <label>{t('proxy_monitor.probe_url')}</label>
                                    <InputText value={probeUrl} onChange={(e) => setProbeUrl(e.target.value)}
                                               placeholder="https://www.pazarama.com/...-p-..."/>
                                </div>
                                <div style={{display: 'flex', gap: '0.6em'}}>
                                    <div style={{flex: 1}}><label>{t('proxy_monitor.capabilities')}</label>
                                        <InputText value={probeCapability} onChange={(e) => setProbeCapability(e.target.value)}
                                                   placeholder="pazarama-ok"/></div>
                                    <div style={{flex: 1}}><label>{t('proxy_monitor.probe_expect')}</label>
                                        <InputText value={probeExpect} onChange={(e) => setProbeExpect(e.target.value)}
                                                   placeholder={'"@type":"product"'}/></div>
                                </div>
                                <small style={{color: 'var(--text-color-secondary)'}}>{t('proxy_monitor.probe_hint')}</small>
                                <div><Button label={t('proxy_monitor.run_probe')} icon="pi pi-play" className="p-button-sm"
                                             onClick={runProbe} loading={probeRunning}
                                             disabled={probeRunning || !probeUrl.trim() || !probeCapability.trim()}/></div>
                                {probeRunning && <div style={{textAlign: 'center'}}><Loading/></div>}
                                {probeResults &&
                                    <div>
                                        <div style={{fontWeight: 600, marginBottom: '0.4em'}}>
                                            {probeResults.ok}/{probeResults.total} {t('proxy_monitor.passed')} · {(probeResults.tagged || []).length} {t('proxy_monitor.tagged')}</div>
                                        <DataTable value={probeResults.results} dataKey="ip"
                                                   paginator={probeResults.results.length > 50} rows={50}>
                                            <Column field="ip" header={t('proxy_monitor.ip')} sortable={true}/>
                                            <Column field="status" header={t('proxy_monitor.status')}/>
                                            <Column header={t('proxy_monitor.result')} body={(r) =>
                                                <Tag value={r.ok ? t('proxy_monitor.pass')
                                                        : (r.blocked ? t('proxy_monitor.blocked') : (r.error || t('proxy_monitor.fail')))}
                                                     severity={r.ok ? 'success' : (r.blocked ? 'danger' : 'warning')}/>}/>
                                        </DataTable>
                                    </div>}
                            </div>
                            : section(scopeCard,
                                scopeCard === 'capabilities'
                                    ? capabilityOptions.filter(c => !c.includes(':')).map(c => ({label: c, value: c}))
                                    : siteOptions,
                                scopeCard === 'capabilities' ? t('proxy_monitor.add_capability') : t('proxy_monitor.select_sites'))}
                        </div>
                    </div>;
                })()}
            </Dialog>

            <Dialog header={t('proxy_monitor.inventory') + (inventoryProvider ? ' — ' + inventoryProvider : '')}
                    visible={inventoryProvider != null}
                    style={{maxWidth: '70vw', minWidth: '30vw'}} breakpoints={{'960px': '90vw', '640px': '95vw'}}
                    modal={true} onHide={() => setInventoryProvider(null)}>
                <DataTable value={inventoryProvider ? ownedRows().filter(r => r.provider === inventoryProvider) : []}
                           emptyMessage={t('proxy_monitor.no_inventory')} paginator={true} rows={10}>
                    <Column body={IndexTemplate} headerStyle={{width: '3em'}}/>
                    <Column field="product" header={t('proxy_monitor.product')} sortable={true}/>
                    <Column field="plan" header={t('proxy_monitor.plan')} sortable={true}/>
                    <Column field="quantity" header={t('proxy_monitor.quantity')} sortable={true}/>
                    <Column header={t('proxy_monitor.est_cost')} sortable={true} sortField="est_cost"
                            body={(r) => r.est_cost != null ? ('~' + r.est_cost + ' ' + (r.currency || '')) : '-'}/>
                    <Column field="expire_date" header={t('proxy_monitor.expiry')} sortable={true}/>
                    <Column header={t('proxy_monitor.auto_renew')}
                            body={(r) => <Tag value={r.auto_renew ? t('proxy_monitor.on') : t('proxy_monitor.off')}
                                              severity={r.auto_renew ? 'success' : 'warning'}/>}/>
                    <Column field="status" header={t('proxy_monitor.status')} sortable={true}/>
                </DataTable>
            </Dialog>

            <Dialog header={catalogProduct ? (catalogProduct.provider + ' — ' + catalogProduct.product) : ''}
                    visible={catalogProduct != null}
                    style={{maxWidth: '60vw', minWidth: '30vw'}} breakpoints={{'960px': '90vw', '640px': '95vw'}}
                    modal={true} onHide={() => setCatalogProduct(null)}>
                <div style={{display: 'flex', gap: '0.8em', alignItems: 'center', marginBottom: '1em', flexWrap: 'wrap'}}>
                    {catalogProduct && trTag(catalogProduct.tr)}
                    {catalogProduct && catalogProduct.discounts &&
                        <span style={{color: 'var(--text-color-secondary)', fontSize: '0.9em'}}>
                            {t('proxy_monitor.discounts')}: {catalogProduct.discounts}</span>}
                </div>

                {catalogProduct && catalogProduct.provider === 'proxyseller' &&
                    <div style={{border: '1px solid var(--surface-border)', borderRadius: '8px', padding: '1em', marginBottom: '1.5em'}}>
                        <h5 style={{marginTop: 0}}>{t('proxy_monitor.price_calculator')}</h5>
                        <div className="p-grid p-fluid" style={{alignItems: 'flex-end'}}>
                            <div className="p-col-6 p-md-3"><label>{t('proxy_monitor.period')}</label>
                                <Dropdown value={quotePeriod} placeholder={t('proxy_monitor.period')} style={{width: '100%'}}
                                          options={(catalogProduct.plans || []).map(pl => ({label: pl.name, value: pl.id}))}
                                          onChange={(e) => setQuotePeriod(e.value)}/></div>
                            <div className="p-col-6 p-md-4"><label>{t('proxy_monitor.country')}</label>
                                <Dropdown value={quoteCountry} placeholder={t('proxy_monitor.country')} filter={true} showClear={true}
                                          style={{width: '100%'}}
                                          options={(catalogProduct.locations || []).map(l => ({label: l.name, value: l.id}))}
                                          onChange={(e) => setQuoteCountry(e.value)}/></div>
                            <div className="p-col-6 p-md-2"><label>{t('proxy_monitor.quantity')}</label>
                                <InputNumber value={quoteQty} min={1} showButtons style={{width: '100%'}}
                                             onValueChange={(e) => setQuoteQty(e.value)}/></div>
                            <div className="p-col-6 p-md-3">
                                <Button label={t('proxy_monitor.calculate')} icon="pi pi-calculator" className="p-button-success"
                                        onClick={calcQuote} disabled={quoteLoading || quotePeriod == null || quoteCountry == null}/></div>
                        </div>
                        {quoteLoading ? <div style={{textAlign: 'center'}}><Loading/></div> :
                            (quote && (quote.error
                                ? <div style={{color: 'var(--red-500)', marginTop: '0.5em'}}>{quote.error}</div>
                                : <div style={{display: 'flex', gap: '1.5em', alignItems: 'baseline', marginTop: '0.8em', flexWrap: 'wrap'}}>
                                    <div>
                                        <div style={{fontSize: '1.5em', fontWeight: 600, lineHeight: 1}}>{quote.total} {quote.currency}</div>
                                        <div style={{fontSize: '0.8em', color: 'var(--text-color-secondary)'}}>{t('proxy_monitor.total')}</div>
                                    </div>
                                    <div style={{color: 'var(--text-color-secondary)'}}>{quote.price} {quote.currency} / {t('proxy_monitor.per_proxy')}</div>
                                    {quote.discount ? <Tag value={t('proxy_monitor.discount') + ': ' + quote.discount} severity="success"/> : null}
                                    <div style={{color: 'var(--text-color-secondary)'}}>{t('proxy_monitor.balance')}: {quote.balance} {quote.currency}</div>
                                    {quote.warning ? <div style={{color: '#b45309'}}>{quote.warning}</div> : null}
                                </div>))}
                    </div>}

                {catalogProduct && catalogProduct.provider !== 'proxyseller'
                    && (catalogProduct.plans || []).some(pl => pl.price != null) &&
                    <div style={{border: '1px solid var(--surface-border)', borderRadius: '8px', padding: '1em', marginBottom: '1.5em'}}>
                        <h5 style={{marginTop: 0}}>{t('proxy_monitor.price_calculator')}</h5>
                        <div className="p-grid p-fluid" style={{alignItems: 'flex-end'}}>
                            <div className="p-col-6 p-md-5"><label>{t('proxy_monitor.plan')}</label>
                                <Dropdown value={iproyalPlan} placeholder={t('proxy_monitor.plan')} style={{width: '100%'}}
                                          options={(catalogProduct.plans || []).filter(pl => pl.price != null).map(pl =>
                                              ({label: pl.name + ' — ' + pl.price + ' ' + (catalogProduct.currency || 'USD'), value: pl.id}))}
                                          onChange={(e) => setIproyalPlan(e.value)}/></div>
                            <div className="p-col-6 p-md-3"><label>{t('proxy_monitor.quantity')}</label>
                                <InputNumber value={iproyalQty} min={1} showButtons style={{width: '100%'}}
                                             onValueChange={(e) => setIproyalQty(e.value)}/></div>
                        </div>
                        {(() => {
                            const r = iproyalCalc();
                            return r ? <div style={{display: 'flex', gap: '1.5em', alignItems: 'baseline', marginTop: '0.8em', flexWrap: 'wrap'}}>
                                <div>
                                    <div style={{fontSize: '1.5em', fontWeight: 600, lineHeight: 1}}>{r.total.toFixed(2)} {r.currency}</div>
                                    <div style={{fontSize: '0.8em', color: 'var(--text-color-secondary)'}}>{t('proxy_monitor.total')}</div>
                                </div>
                                <div style={{color: 'var(--text-color-secondary)'}}>{r.per.toFixed(2)} {r.currency} / {t('proxy_monitor.per_proxy')}</div>
                                {r.disc ? <Tag value={t('proxy_monitor.discount') + ': ' + r.disc + '%'} severity="success"/> : null}
                            </div> : null;
                        })()}
                    </div>}

                <DataTable value={catalogProduct ? catalogProduct.plans : []}
                           emptyMessage={t('proxy_monitor.no_catalog')} paginator={true} rows={10}>
                    <Column body={IndexTemplate} headerStyle={{width: '3em'}}/>
                    <Column field="name" header={t('proxy_monitor.plan')} sortable={true}/>
                    <Column header={t('proxy_monitor.price')} sortable={true} sortField="price"
                            body={(pl) => pl.price != null ? (pl.price + ' ' + (catalogProduct.currency || 'USD'))
                                : t('proxy_monitor.price_at_order')}/>
                    <Column field="min_quantity" header={t('proxy_monitor.min_qty')} sortable={true}/>
                    <Column field="max_quantity" header={t('proxy_monitor.max_qty')} sortable={true}/>
                </DataTable>
                <h5 style={{marginTop: '1.5em'}}>{t('proxy_monitor.countries')}</h5>
                <DataTable value={catalogProduct ? catalogProduct.locations : []}
                           emptyMessage={t('proxy_monitor.no_catalog')} paginator={true} rows={10}>
                    <Column body={IndexTemplate} headerStyle={{width: '3em'}}/>
                    <Column field="name" header={t('proxy_monitor.country')} sortable={true}/>
                    <Column header={t('proxy_monitor.stock')}
                            body={(loc) => loc.out_of_stock
                                ? <Tag value={t('proxy_monitor.out_of_stock')} severity="danger"/>
                                : <Tag value={t('proxy_monitor.in_stock')} severity="success"/>}/>
                </DataTable>
            </Dialog>

            <TabView activeIndex={activeTab} onTabChange={(e) => onTabChange(e.index)}>
                <TabPanel header={t('proxy_monitor.tab_pool')}>
                    {summary &&
                        <div style={{display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap',
                                     paddingBottom: '1em', marginBottom: '1em', borderBottom: '0.5px solid var(--surface-border)'}}>
                            <span style={{display: 'inline-flex', alignItems: 'center', gap: '7px', fontWeight: 600}}>
                                <span style={{width: '9px', height: '9px', borderRadius: '50%', display: 'inline-block',
                                              background: summary.disabled ? '#f59e0b' : '#22c55e'}}/>
                                {summary.total} {t('proxy_monitor.proxies_word')}</span>
                            <span style={{color: 'var(--text-color-secondary)', fontSize: '0.9em'}}>
                                {summary.enabled} {t('proxy_monitor.enabled')}
                                {summary.disabled ? ' · ' + summary.disabled + ' ' + t('proxy_monitor.disabled') : ''}</span>
                            {providerCounts.map(g => {
                                const ps = providerStyle(g.source);
                                return <span key={g.source} style={{fontSize: '0.82em', fontWeight: 500, background: ps.bg,
                                             color: ps.fg, padding: '3px 12px', borderRadius: '20px', textTransform: 'capitalize'}}>
                                    {g.source} {g.count}</span>;
                            })}
                            <span style={{marginLeft: 'auto', display: 'inline-flex', gap: '0.4em'}}>
                                <Button label={t('proxy_monitor.sync_now')}
                                        icon={syncing ? 'pi pi-spin pi-spinner' : 'pi pi-cloud-download'}
                                        className="p-button-outlined p-button-sm" onClick={syncNow} disabled={syncing}/>
                                <Button label={t('proxy_monitor.activity')} icon="pi pi-history"
                                        className="p-button-outlined p-button-sm" onClick={() => setShowLogs(true)}/>
                                <Button icon="pi pi-refresh" className="p-button-outlined p-button-sm" onClick={load}
                                        tooltip={t('actions.refresh')} tooltipOptions={{position: 'bottom'}}/>
                            </span>
                        </div>}

                    <div className="p-grid p-fluid" style={{alignItems: 'flex-end', marginBottom: '1em'}}>
                        <div className="p-col-6 p-md-2"><label>{t('proxy_monitor.source')}</label>{dd('source', distinct('source'))}</div>
                        <div className="p-col-6 p-md-2"><label>{t('proxy_monitor.status')}</label>{dd('enabled', statusOptions)}</div>
                        <div className="p-col-6 p-md-2"><label>{t('proxy_monitor.serves')}</label>{dd('serves', serveOptions)}</div>
                        <div className="p-col-6 p-md-2"><label>{t('proxy_monitor.capabilities')}</label>{dd('cap', flatDistinct('capabilities'))}</div>
                        <div className="p-col-6 p-md-2" style={{display: 'flex', alignItems: 'center', paddingBottom: '4px'}}>
                            <span onClick={() => setFilters({...filters, attention: !filters.attention})}
                                  style={{cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '6px',
                                          fontSize: '0.85em', padding: '6px 12px', borderRadius: '20px', whiteSpace: 'nowrap',
                                          background: filters.attention ? 'rgba(245,158,11,0.18)' : 'var(--surface-100)',
                                          color: filters.attention ? '#b45309' : 'var(--text-color-secondary)'}}>
                                <i className="pi pi-exclamation-triangle" style={{fontSize: '0.85em'}}/>
                                {t('proxy_monitor.needs_attention')} ({attentionCount})
                            </span>
                        </div>
                    </div>

                    {selectedProxies.length > 0
                        ? <div style={{display: 'flex', gap: '0.6em', alignItems: 'center', marginBottom: '0.8em'}}>
                            <span style={{fontWeight: 500, fontSize: '0.9em'}}>{selectedProxies.length} {t('proxy_monitor.selected')}</span>
                            <Button label={t('proxy_monitor.edit_scope')} icon="pi pi-pencil" className="p-button-outlined p-button-sm"
                                    onClick={() => openScope(selectedProxies.map(p => p.ip))}/>
                        </div>
                        : <div style={{marginBottom: '0.8em', fontSize: '0.85em', color: 'var(--text-color-secondary)',
                                       display: 'flex', alignItems: 'center', gap: '6px'}}>
                            <i className="pi pi-info-circle" style={{fontSize: '0.85em'}}/>
                            {t('proxy_monitor.select_hint')}
                        </div>}
                    {loading ?
                        <div className="p-col-12" style={{textAlign: 'center'}}><Loading/></div> :
                        <DataTable value={displayed} emptyMessage={t('actions.empty_message')} dataKey="ip"
                                   paginator={displayed.length > 50} rows={50}
                                   selection={selectedProxies} onSelectionChange={(e) => setSelectedProxies(e.value)}>
                                <Column selectionMode="multiple" headerStyle={{width: '3em'}}/>
                                <Column header={t('proxy_monitor.proxy')} body={proxyBody} sortable={true} sortField="ip"/>
                                <Column field="enabled" header={t('proxy_monitor.status')} body={statusBody}
                                        sortable={true} sortField="enabled"/>
                                <Column header={t('proxy_monitor.requests')} body={reqBody} headerStyle={{width: '7em'}}/>
                                <Column header={t('proxy_monitor.success_rate')} body={poolSuccessBody} headerStyle={{width: '8em'}}/>
                                <Column header={t('proxy_monitor.serves')} body={servesBody}/>
                            </DataTable>}
                </TabPanel>

                <TabPanel header={t('proxy_monitor.tab_site_rules')}>
                    <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1em'}}>
                        <span style={{color: 'var(--text-color-secondary)', fontSize: '0.9em'}}>{t('proxy_monitor.site_rules_hint')}</span>
                        <Button label={t('proxy_monitor.add_rule')} icon="pi pi-plus" className="p-button-success p-button-sm"
                                onClick={() => setSiteRuleTarget({host: '', groups: [[]], activeGroup: 0, max_concurrency: 0, existing: false})}/>
                    </div>
                    <DataTable value={siteRules} emptyMessage={t('proxy_monitor.no_site_rules')} dataKey="host"
                               paginator={siteRules.length > 50} rows={50}>
                        <Column body={IndexTemplate} headerStyle={{width: '3em'}}/>
                        <Column field="host" header={t('proxy_monitor.host')} sortable={true}/>
                        <Column header={t('proxy_monitor.requires')} body={(r) => {
                            // Render OR-groups: each group's caps as green tags, groups joined by an
                            // "OR" pill. A single-group (flat) rule looks exactly as before.
                            const groups = asGroups(r.requires);
                            const nodes = [];
                            groups.forEach((g, gi) => {
                                if (gi > 0) nodes.push(<span key={'or' + gi} style={{fontSize: '0.7em', fontWeight: 700,
                                    color: 'var(--text-color-secondary)', border: '1px solid var(--surface-border)',
                                    borderRadius: '8px', padding: '0 5px'}}>{t('proxy_monitor.or')}</span>);
                                g.forEach(c => nodes.push(<Tag key={gi + '-' + c} value={c}
                                    style={{background: 'rgba(34,197,94,0.14)', color: '#16a34a'}}/>));
                            });
                            return <span style={{display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '4px'}}>
                                {r.requires_render && <Tag value="JS render"
                                    style={{background: 'rgba(37,99,235,0.14)', color: '#2563eb'}}/>}
                                {r.max_concurrency > 0 && <Tag value={'⚡ max ' + r.max_concurrency}
                                    style={{background: 'rgba(234,179,8,0.16)', color: '#a16207'}}/>}
                                {nodes.length === 0 && !r.requires_render && !r.max_concurrency &&
                                    <span style={{color: 'var(--text-color-secondary)', fontSize: '0.85em'}}>
                                        {t('proxy_monitor.open_site')}</span>}
                                {nodes}
                            </span>;
                        }}/>
                        <Column header={t('table_headers.action')} headerStyle={{width: '8em'}} body={(r) =>
                            <span style={{display: 'flex', gap: '2px'}}>
                                <Button icon="pi pi-pencil" className="p-button-text p-button-sm" tooltip={t('actions.edit')}
                                        onClick={() => setSiteRuleTarget({host: r.host,
                                            groups: asGroups(r.requires).length ? asGroups(r.requires).map(g => [...g]) : [[]],
                                            activeGroup: 0, requires_render: !!r.requires_render,
                                            max_concurrency: r.max_concurrency || 0, existing: true})}/>
                                <Button icon="pi pi-trash" className="p-button-danger p-button-text p-button-sm"
                                        tooltip={t('actions.remove')} onClick={() => removeSiteRule(r.host)}/>
                            </span>}/>
                    </DataTable>

                    <div style={{marginTop: '1.6em', borderTop: '1px solid var(--surface-border)', paddingTop: '1.2em'}}>
                        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.3em'}}>
                            <span style={{fontWeight: 600, display: 'flex', alignItems: 'center', gap: '8px'}}>
                                <i className="pi pi-sliders-h" style={{color: '#2563eb'}}/>{t('proxy_monitor.adaptive_title')}
                                <Tag value={selectionEnabled ? t('proxy_monitor.adaptive_on') : t('proxy_monitor.adaptive_off')}
                                     severity={selectionEnabled ? 'success' : 'warning'}/>
                            </span>
                            <span style={{display: 'flex', gap: '0.5em'}}>
                                <Button label={t('proxy_monitor.adaptive_params')} icon="pi pi-cog"
                                        className="p-button-outlined p-button-secondary p-button-sm"
                                        onClick={() => setShowParams(true)}/>
                                <Button label={selectionEnabled ? t('proxy_monitor.adaptive_disable') : t('proxy_monitor.adaptive_enable')}
                                        icon={selectionEnabled ? 'pi pi-pause' : 'pi pi-play'}
                                        className="p-button-outlined p-button-sm" onClick={toggleSelectionEnabled}/>
                            </span>
                        </div>
                        <span style={{color: 'var(--text-color-secondary)', fontSize: '0.9em'}}>{t('proxy_monitor.adaptive_hint')}</span>

                        <div style={{display: 'flex', flexWrap: 'wrap', gap: '6px', margin: '0.8em 0'}}>
                            {selectionHosts.length === 0 &&
                                <span style={{fontSize: '0.85em', color: 'var(--text-color-secondary)'}}>{t('proxy_monitor.adaptive_none')}</span>}
                            {selectionHosts.map(h => {
                                const n = (selectionWeights[h] || []).length;
                                return <span key={h} style={{display: 'inline-flex', alignItems: 'center', gap: '6px',
                                             fontSize: '0.82em', borderRadius: '12px', padding: '3px 8px 3px 11px', whiteSpace: 'nowrap',
                                             background: 'rgba(37,99,235,0.10)', color: '#2563eb', opacity: selectionEnabled ? 1 : 0.5}}>
                                    {shortHost(h)}{n ? <span style={{opacity: 0.7}}>· {n}</span> : ''}
                                    <i className="pi pi-times-circle" title={t('actions.remove')}
                                       style={{cursor: 'pointer', fontSize: '0.95em', opacity: 0.8}}
                                       onClick={() => removeSelectionHost(h)}/></span>;
                            })}
                        </div>

                        <div style={{display: 'flex', gap: '0.5em', maxWidth: '520px'}}>
                            <Dropdown value={selectionHostPick}
                                      options={siteOptions.filter(o => !selectionHosts.includes(o.value))}
                                      filter={true} showClear={true} emptyMessage={t('proxy_monitor.no_sites')}
                                      onChange={(e) => setSelectionHostPick(e.value)}
                                      placeholder={t('proxy_monitor.adaptive_add_placeholder')} style={{flex: 1, minWidth: 0}}/>
                            <Button label={t('proxy_monitor.op_add')} icon="pi pi-plus" className="p-button-sm"
                                    style={{width: 'auto', flexShrink: 0}}
                                    onClick={() => addSelectionHost(selectionHostPick)} disabled={!selectionHostPick}/>
                        </div>
                    </div>
                </TabPanel>

                <TabPanel header={t('proxy_monitor.tab_traffic')}>
                    <div className="p-grid p-fluid" style={{alignItems: 'flex-end', marginBottom: '1em'}}>
                        <div className="p-col-6 p-md-2"><label>{t('proxy_monitor.range')}</label>
                            <Dropdown value={statsDays} options={daysOptions} style={{width: '100%'}}
                                      onChange={(e) => onDaysChange(e.value)}/></div>
                        <div className="p-col-6 p-md-2"><label>{t('proxy_monitor.group_by')}</label>
                            <Dropdown value={groupBy} options={groupOptions} style={{width: '100%'}}
                                      onChange={(e) => setGroupBy(e.value)}/></div>
                    </div>

                    {summaryTags}

                    {statsLoading ?
                        <div className="p-col-12" style={{textAlign: 'center'}}><Loading/></div> :
                        <DataTable value={pivot(statsRows, groupBy)} emptyMessage={t('proxy_monitor.no_traffic')}
                                   dataKey="key" paginator={statsRows.length > 50} rows={50}>
                            <Column body={IndexTemplate} headerStyle={{width: '3em'}}/>
                            <Column field="key" header={t('proxy_monitor.' + groupBy)} body={groupBody} sortable={true}/>
                            {trafficColumns()}
                        </DataTable>}
                </TabPanel>

                <TabPanel header={t('proxy_monitor.tab_catalog')}>
                    {storeLoading ?
                        <div className="p-col-12" style={{textAlign: 'center'}}><Loading/></div> :
                        <React.Fragment>
                            {comparison.length > 0 &&
                                <React.Fragment>
                                    <h5>{t('proxy_monitor.comparison')} — 100 IP · {t('proxy_monitor.region_tr_name')}</h5>
                                    <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(265px, 1fr))',
                                                 gap: '14px', margin: '0.5em 0 2em'}}>
                                        {comparison.map(r => {
                                            const ipT = r.iproyal ? r.iproyal.total : null;
                                            const psT = r.proxyseller ? r.proxyseller.total : null;
                                            const ipCheaper = ipT != null && (psT == null || ipT <= psT);
                                            const psCheaper = psT != null && (ipT == null || psT < ipT);
                                            const fmt = (v, c) => v != null ? (Number(v).toFixed(2) + ' ' + (c || 'USD')) : '—';
                                            const row = (name, val, cheaper) =>
                                                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '0.4em'}}>
                                                    <span style={{textTransform: 'capitalize'}}>{name}</span>
                                                    <span style={{display: 'flex', alignItems: 'center', gap: '6px'}}>
                                                        <b style={{color: cheaper ? '#16a34a' : 'var(--text-color)'}}>{val}</b>
                                                        {cheaper && <Tag value={t('proxy_monitor.cheaper')} severity="success"/>}
                                                    </span>
                                                </div>;
                                            return <div key={r.key} style={{background: 'var(--surface-card)', borderRadius: '12px',
                                                                            border: '1px solid var(--surface-border)', padding: '1em 1.25em'}}>
                                                <div style={{fontWeight: 600}}>{r.label} · 100 IP · TR</div>
                                                {row('iproyal', fmt(ipT, r.iproyal && r.iproyal.currency), ipCheaper)}
                                                {row('proxyseller', comparisonLoading ? '…'
                                                    : (psT != null ? fmt(psT, r.proxyseller.currency) : (r.psError || '—')), psCheaper)}
                                            </div>;
                                        })}
                                    </div>
                                </React.Fragment>}

                            <h5>{t('proxy_monitor.inventory')}</h5>
                            <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(235px, 1fr))',
                                         gap: '14px', margin: '0.5em 0 2em'}}>
                                {((store && store.providers) || []).map(pr => {
                                    const ow = pr.owned || {};
                                    const hasOrders = ((ow.orders) || []).length > 0;
                                    const ps = providerStyle(pr.provider);
                                    return <div key={pr.provider}
                                                onClick={() => hasOrders && setInventoryProvider(pr.provider)}
                                                style={{cursor: hasOrders ? 'pointer' : 'default',
                                                    background: 'var(--surface-card)', borderRadius: '12px',
                                                    border: '1px solid var(--surface-border)', padding: '1em 1.25em'}}>
                                        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.8em'}}>
                                            <span style={{width: '38px', height: '38px', borderRadius: '10px', display: 'flex',
                                                          alignItems: 'center', justifyContent: 'center', background: ps.bg, color: ps.fg}}>
                                                <i className="pi pi-server" style={{fontSize: '1.1em'}}/></span>
                                            <span style={{display: 'flex', alignItems: 'center', gap: '6px'}}>
                                                {hasOrders && <i className="pi pi-external-link" style={{fontSize: '0.75em', color: 'var(--text-color-secondary)'}}/>}
                                                <span style={{fontSize: '0.72em', fontWeight: 600, padding: '2px 9px', borderRadius: '20px',
                                                              background: ps.bg, color: ps.fg, textTransform: 'capitalize'}}>{pr.provider}</span>
                                            </span>
                                        </div>
                                        {pr.owned_error
                                            ? <span style={{color: 'var(--text-color-secondary)'}}>{t('proxy_monitor.no_inventory')}</span>
                                            : <div style={{fontSize: '1.7em', fontWeight: 600, lineHeight: 1}}>{ow.count || 0}
                                                <span style={{fontSize: '0.48em', color: 'var(--text-color-secondary)', fontWeight: 400, marginLeft: '5px'}}>IP</span></div>}
                                        <div style={{marginTop: '0.9em', paddingTop: '0.7em', borderTop: '1px solid var(--surface-border)',
                                                     display: 'flex', flexDirection: 'column', gap: '5px', fontSize: '0.85em', color: 'var(--text-color-secondary)'}}>
                                            {ow.est_cost != null &&
                                                <span><i className="pi pi-dollar" style={{marginRight: '7px'}}/>{t('proxy_monitor.est_cost')}:{' '}
                                                    <b style={{color: 'var(--text-color)'}}>~{ow.est_cost} {pr.currency || 'USD'}</b></span>}
                                            <span><i className="pi pi-calendar" style={{marginRight: '7px'}}/>{t('proxy_monitor.next_expiry')}:{' '}
                                                <b style={{color: 'var(--text-color)'}}>{ow.next_expiry || '-'}</b></span>
                                            <span><i className="pi pi-wallet" style={{marginRight: '7px'}}/>{t('proxy_monitor.balance')}:{' '}
                                                <b style={{color: 'var(--text-color)'}}>{pr.balance != null ? (pr.balance + ' ' + (pr.currency || 'USD')) : '—'}</b></span>
                                        </div>
                                    </div>;
                                })}
                            </div>

                            {((store && store.providers) || []).filter(pr => pr.owned_error || pr.balance_error).map(pr =>
                                <div key={pr.provider} style={{color: 'var(--red-500)', fontSize: '0.85em', marginBottom: '0.3em'}}>
                                    {pr.provider}: {pr.owned_error || pr.balance_error}</div>)}

                            <h5>{t('proxy_monitor.products_title')}</h5>
                            {((store && store.providers) || []).filter(pr => pr.products_error).map(pr =>
                                <div key={pr.provider} style={{color: 'var(--red-500)', fontSize: '0.85em', marginBottom: '0.5em'}}>
                                    {pr.provider}: {pr.products_error}</div>)}
                            {catalogProducts().length === 0 &&
                                <div style={{color: 'var(--text-color-secondary)'}}>{t('proxy_monitor.no_catalog')}</div>}
                            <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(235px, 1fr))',
                                         gap: '14px', margin: '0.5em 0'}}>
                                {catalogProducts().map(p => {
                                    const price = p.minPrice == null ? '-'
                                        : (p.minPrice === p.maxPrice ? ('' + p.minPrice) : (p.minPrice + '–' + p.maxPrice));
                                    const ps = providerStyle(p.provider);
                                    const chip = {display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '0.78em',
                                        color: 'var(--text-color-secondary)', border: '1px solid var(--surface-border)',
                                        padding: '3px 9px', borderRadius: '20px'};
                                    return <div key={p.key} onClick={() => openProduct(p)}
                                                style={{cursor: 'pointer', background: 'var(--surface-card)', borderRadius: '12px',
                                                    border: '1px solid var(--surface-border)', padding: '1em 1.25em'}}>
                                        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.7em'}}>
                                            <span style={{width: '38px', height: '38px', borderRadius: '10px', display: 'flex',
                                                          alignItems: 'center', justifyContent: 'center', background: ps.bg, color: ps.fg}}>
                                                <i className={'pi ' + productIcon(p.product)} style={{fontSize: '1.1em'}}/></span>
                                            <i className="pi pi-external-link" style={{fontSize: '0.75em', color: 'var(--text-color-secondary)'}}/>
                                        </div>
                                        <div style={{fontWeight: 600}}>{p.product}</div>
                                        <span style={{display: 'inline-block', fontSize: '0.68em', fontWeight: 600, padding: '2px 9px',
                                                      borderRadius: '20px', background: ps.bg, color: ps.fg,
                                                      textTransform: 'capitalize', marginTop: '3px'}}>{p.provider}</span>
                                        {p.minPrice != null
                                            ? <div style={{fontSize: '1.5em', fontWeight: 600, lineHeight: 1, marginTop: '0.6em'}}>{price}
                                                <span style={{fontSize: '0.5em', color: 'var(--text-color-secondary)', fontWeight: 400, marginLeft: '5px'}}>{p.currency || 'USD'}</span></div>
                                            : <div style={{display: 'inline-block', fontSize: '0.8em', fontWeight: 500, padding: '3px 10px',
                                                           borderRadius: '20px', border: '1px solid var(--surface-border)',
                                                           color: 'var(--text-color-secondary)', marginTop: '0.7em'}}>{t('proxy_monitor.price_at_order')}</div>}
                                        <div style={{display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '0.7em'}}>
                                            <span style={chip}><i className="pi pi-clock" style={{fontSize: '0.9em'}}/>{p.planCount} {t('proxy_monitor.plans')}</span>
                                            <span style={chip}><i className="pi pi-globe" style={{fontSize: '0.9em'}}/>{(p.locations || []).length} {t('proxy_monitor.countries')}</span>
                                        </div>
                                        <div style={{display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '0.7em'}}>
                                            {regionTags(p)}
                                        </div>
                                    </div>;
                                })}
                            </div>
                        </React.Fragment>}
                </TabPanel>


            </TabView>
        </div>
    </React.Fragment>;
};
export default ProxyMonitor;
